/**
 * Brokers whose rating the operator has already revealed, so reopening a load doesn't hide it again.
 *
 * This stores ONE BIT per broker — that the rating was asked for — and deliberately not the rating
 * itself. The value is already cached twice (a 60s module cache in `services/factoring/data.ts`, and a
 * three-hour Mongo cache server-side, `FACTORING_RATING_TTL_SECONDS`); a third copy here would be a
 * third TTL to keep honest, and the one most likely to disagree with the other two.
 *
 * What caching cannot supply is this bit. "Has this operator pressed See Rating for this broker" never
 * reached a server, so nothing upstream knows it — and a cache only helps if something asks. A deferred
 * chip asks nothing, so without this the caches sit warm and unused while the button comes back.
 *
 * Ten minutes, not an hour. The behaviour this serves is a dispatcher comparing two or three candidates
 * — open A, check the broker, look at B, come back to A — which plays out in minutes. A longer window
 * wouldn't catch more of that; it would just accumulate into fetch-on-open for every broker touched
 * during a shift, which is the thing the See Rating gate exists to stop.
 *
 * In memory only. A reload is a natural reset, and nothing here is worth persisting.
 */

import { FactoringProvider } from 'services/factoring'

export const REVEAL_MEMORY_TTL_MS = 10 * 60 * 1000

/** Entries kept before the oldest is dropped. An operator can open thousands of loads in a shift, and an
 *  unbounded Map would hold every broker they glanced at for the life of the tab. */
const MAX_ENTRIES = 500

/** key → when it was revealed. */
const revealedAt = new Map<string, number>()

/**
 * Cache key for one broker under one provider.
 *
 * The provider belongs in the key because TAFS and RTS are separate lookups with separate credentials:
 * revealing a broker under TAFS says nothing about whether the operator wanted to spend an RTS lookup
 * on it.
 */
export const revealKey = (provider: FactoringProvider, brokerKey: string): string => `${provider}|${brokerKey}`

/** The broker's identity for this purpose: the MC when the load has one, the company name otherwise (the
 *  Sylectus path). Namespaced so a company named after a number can't collide with an MC. */
export const brokerCacheKey = (mc: string | null, company: string | null): string | null => {
    if (mc) return `mc:${mc}`
    if (company) return `co:${company.trim().toLowerCase()}`
    return null
}

/**
 * Pure on purpose — this is read from a component's render body, and a render is not allowed to have
 * side effects. Dropping the expired entry here would mean StrictMode's double invoke, or any render
 * React discards, mutating the store for a commit that never happened. Harmless while the only mutation
 * is deleting something already expired; not harmless the first time the eviction policy grows.
 * Expired keys are swept in `recordRevealed` instead, which already walks the Map.
 */
export const isRevealed = (key: string): boolean => {
    const at = revealedAt.get(key)
    return at !== undefined && Date.now() - at < REVEAL_MEMORY_TTL_MS
}

/**
 * Ten minutes from the FIRST reveal, not from the latest confirmation.
 *
 * An already-revealed broker opens un-deferred by design, so it fetches and reports on every load from
 * that brokerage. Refreshing the timestamp on those reports made the window slide: on a Sylectus board
 * dominated by a handful of brokerages the entry never expired, and the gate degraded into exactly the
 * fetch-on-open-for-every-broker-touched-this-shift that the ten minutes exists to prevent. An existing
 * unexpired entry is therefore left alone.
 */
export const recordRevealed = (key: string): void => {
    const now = Date.now()
    if (isRevealed(key)) return
    // Sweep here rather than on read: writes are rare (one per press) and already O(size) for the
    // eviction below, so this is where a pass over the Map costs nothing extra.
    for (const [entry, at] of revealedAt) {
        if (now - at >= REVEAL_MEMORY_TTL_MS) revealedAt.delete(entry)
    }
    // Re-inserting moves the key to the end of the Map's insertion order, which is what makes the
    // oldest-first eviction below correct.
    revealedAt.delete(key)
    revealedAt.set(key, now)
    while (revealedAt.size > MAX_ENTRIES) {
        const oldest = revealedAt.keys().next()
        if (oldest.done) break
        revealedAt.delete(oldest.value)
    }
}

/** Test seam. Nothing in the app clears this — entries age out on their own. */
export const clearRevealedRatings = (): void => revealedAt.clear()
